<template>
  <div class="d-flex" style="min-height: calc(100vh - 64px)">
    <!-- Desktop: static sidebar -->
    <v-sheet
      v-if="!mobile"
      width="240"
      class="border-e flex-shrink-0 d-flex flex-column"
    >
      <v-list-item
        v-if="tenantData"
        :title="tenantData.razao_social"
        :subtitle="formatCnpj(tenantData.cnpj)"
        class="mt-4"
      >
        <template v-slot:prepend>
          <v-avatar color="primary" size="40">
            <span class="text-h6 text-white">
              {{ tenantData.razao_social?.charAt(0) || "C" }}
            </span>
          </v-avatar>
        </template>
      </v-list-item>

      <v-divider />

      <v-list density="compact" nav class="flex-grow-1">
        <v-list-item
          v-for="item in navItems"
          :key="item.routeName"
          :to="{ name: item.routeName, params: { tenant_id: tenantId } }"
          :prepend-icon="item.icon"
          :title="item.title"
          :active="isActive(item.routeName)"
          color="primary"
        />
      </v-list>

      <v-divider />

      <v-list density="compact" nav>
        <v-list-item
          :to="{ name: 'tenant.index' }"
          :prepend-icon="mdiArrowLeft"
          title="Voltar para Clientes"
        />
      </v-list>
    </v-sheet>

    <!-- Mobile: temporary drawer -->
    <v-navigation-drawer v-if="mobile" v-model="subDrawer" temporary>
      <v-list-item
        v-if="tenantData"
        :title="tenantData.razao_social"
        :subtitle="formatCnpj(tenantData.cnpj)"
      >
        <template v-slot:prepend>
          <v-avatar color="primary" size="40">
            <span class="text-h6 text-white">
              {{ tenantData.razao_social?.charAt(0) || "C" }}
            </span>
          </v-avatar>
        </template>
      </v-list-item>

      <v-divider />

      <v-list density="compact" nav class="flex-grow-1">
        <v-list-item
          v-for="item in navItems"
          :key="item.routeName"
          :to="{ name: item.routeName, params: { tenant_id: tenantId } }"
          :prepend-icon="item.icon"
          :title="item.title"
          :active="isActive(item.routeName)"
          color="primary"
        />
      </v-list>

      <template v-slot:append>
        <v-divider />
        <v-list density="compact" nav>
          <v-list-item
            :to="{ name: 'tenant.index' }"
            :prepend-icon="mdiArrowLeft"
            title="Voltar para Clientes"
          />
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- Content area -->
    <div class="flex-grow-1 overflow-x-auto">
      <div v-if="mobile" class="d-flex align-center pa-2">
        <v-btn icon variant="text" @click="subDrawer = !subDrawer">
          <v-icon :icon="mdiMenu" />
        </v-btn>
        <span class="text-subtitle-1 ml-2">{{ tenantData?.razao_social }}</span>
      </div>

      <router-view />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import {
  mdiCardAccountDetails,
  mdiDomain,
  mdiAccountKey,
  mdiPalette,
  mdiChartBar,
  mdiArrowLeft,
  mdiMenu,
} from "@mdi/js";
import { useCompanyQuery } from "@/queries/modules/companies";
import { useMetaStore } from "@/stores/meta";
import { useBreadcrumbTitle } from "@/composables/useBreadcrumbs";

const route = useRoute();
const metaStore = useMetaStore();
const { mobile } = useDisplay();

const subDrawer = ref(false);

const navItems = [
  {
    routeName: "tenant.detail",
    title: "Detalhes Cliente",
    icon: mdiCardAccountDetails,
  },
  { routeName: "tenant.companies", title: "Empresas", icon: mdiDomain },
  { routeName: "tenant.grants", title: "Acessos", icon: mdiAccountKey },
  { routeName: "tenant.branding", title: "Whitelabel", icon: mdiPalette },
  { routeName: "tenant.billing", title: "Relatórios", icon: mdiChartBar },
];

const isActive = (routeName) => {
  return route.matched.some((r) => r.name === routeName);
};

const tenantId = computed(() => {
  return route.params.tenant_id ? Number(route.params.tenant_id) : null;
});

const { data: tenantData } = useCompanyQuery(tenantId);

const tenantName = computed(() => tenantData.value?.razao_social);
useBreadcrumbTitle(tenantName);

const formatCnpj = (cnpj) => {
  if (!cnpj) return "";
  const cleaned = cnpj.replace(/\D/g, "");
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

watch(
  tenantData,
  (data) => {
    if (data) {
      metaStore.setTitle(data.razao_social || "Detalhes do Cliente");
    }
  },
  { immediate: true },
);
</script>
